import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Factory,
  MapPin,
  Package,
  RefreshCcw,
  ShoppingBag,
  Truck,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import {
  buildDailyLogisticsSummary,
  buildOperationalRowsForDate,
  buildProductMap,
  buildSaleItemsBySaleId,
  fetchLogisticsMonthDataset,
  getLogisticsMonthRange,
} from '../utils/logisticsPlanning';
import {
  filterOperationalProductionRequestLines,
  reconcileProductionRequestLinesWithOrders,
} from '../utils/productionOrders';
import { applyActiveProductsFilter } from '../utils/productStatus';

const EMPTY_DAY = { ordersCount: 0, pendingUnits: 0, productionUnits: 0, productsCount: 0 };

const fmt = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const OPERATIONAL_COLUMNS = [
  {
    key: 'name',
    label: 'Producto',
    type: 'text',
    align: 'left',
    defaultDirection: 'asc',
    tooltip: 'Producto consolidado para la fecha seleccionada en el sitio elegido.',
  },
  {
    key: 'stock',
    label: 'Stock',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Stock operativo visible para este producto y sitio. Si inventario quedo en negativo por vender sin stock, aqui se muestra 0.',
  },
  {
    key: 'sentToProduction',
    label: 'Enviado a produccion',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Unidades solicitadas hoy a produccion para cubrir la logistica del dia.',
  },
  {
    key: 'productionPending',
    label: 'Pendiente de produccion',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Unidades que siguen pendientes de producir para este dia. Se calcula como enviado a produccion menos produccion completada.',
  },
  {
    key: 'completedProduction',
    label: 'Produccion completada',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Unidades que ya se fabricaron para este producto en la fecha operativa seleccionada.',
  },
  {
    key: 'pendingOrders',
    label: 'Pedidos pendientes',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Unidades que todavia faltan entregar hoy. Cuando una entrega se completa, baja este numero.',
  },
  {
    key: 'saldo',
    label: 'Saldo faltante',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Cantidad que todavia falta resolver para cumplir los pedidos del dia. Se calcula cuando stock + pendiente de produccion + produccion completada - pedidos pendientes da negativo.',
  },
  {
    key: 'closingProjection',
    label: 'Cierre proyectado',
    type: 'number',
    align: 'right',
    defaultDirection: 'desc',
    tooltip: 'Stock operativo estimado al cierre del dia. Se calcula como stock + pendiente de produccion + produccion completada - pedidos pendientes.',
  },
];

const EMPTY_COLUMN_FILTERS = {
  name: '',
  stock: '',
  sentToProduction: '',
  productionPending: '',
  completedProduction: '',
  pendingOrders: '',
  saldo: '',
  closingProjection: '',
};

const matchesNumericFilter = (value, rawFilter) => {
  const normalized = String(rawFilter || '').trim().replace(',', '.');
  if (!normalized) return true;

  const match = normalized.match(/^(>=|<=|>|<|=)?\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return String(value).includes(normalized);

  const operator = match[1] || '>=';
  const target = Number(match[2]);
  if (!Number.isFinite(target)) return true;

  if (operator === '>') return value > target;
  if (operator === '>=') return value >= target;
  if (operator === '<') return value < target;
  if (operator === '<=') return value <= target;
  return value === target;
};

export default function LogisticaOperativa() {
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  // ✅ FIX bug #2 multi-sucursal: SiteContext es la fuente única de verdad. Ya no
  // leemos localStorage al montar — el cambio de sucursal del Topbar se refleja en
  // tiempo real.
  const { selectedSiteId: contextSiteId, setSelectedSiteId: setContextSiteId } = useSite();
  const selectedSiteId = contextSiteId || '';
  const setSelectedSiteId = (next) => setContextSiteId(next || '');
  const [sitios, setSitios] = useState([]);
  const [orders, setOrders] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [productions, setProductions] = useState([]);
  const [productionRequestLines, setProductionRequestLines] = useState([]);
  const [scopedProducts, setScopedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoaded, setAllProductsLoaded] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'pendingOrders', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const fetchDataRef = useRef(() => {});
  const realtimeRefreshTimeoutRef = useRef(null);

  const range = useMemo(() => getLogisticsMonthRange(currentDate), [currentDate]);

  useEffect(() => {
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    if (selectedDay > daysInMonth) setSelectedDay(daysInMonth);
  }, [currentDate, selectedDay]);

  // ✅ FIX bug #2: la persistencia ahora la maneja SiteContext (org-scoped). Removido
  // el efecto de escritura directa a localStorage para evitar duplicación de fuentes.

  useEffect(() => {
    setAllProducts([]);
    setAllProductsLoaded(false);
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, currentDate, selectedSiteId]);

  useEffect(() => {
    if (!profile?.organization_id) return undefined;

    const organizationId = profile.organization_id;
    const scheduleRefresh = () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
      }

      realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
        fetchDataRef.current?.();
      }, 250);
    };

    const channel = supabase
      .channel(`logistica_operativa_store_orders_${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'store_orders',
          filter: `organization_id=eq.${organizationId}`,
        },
        scheduleRefresh,
      )
      .subscribe();

    return () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [profile?.organization_id]);

  const loadBaseData = async () => {
    try {
      const orgId = profile.organization_id;
      const sitesRes = await supabase
        .from('sitios')
        .select('id, nombre')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('nombre');

      if (sitesRes.error) throw sitesRes.error;

      setSitios(sitesRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la base de logistica.');
    }
  };

  const fetchData = async () => {
    const firstLoad = loading;
    if (!firstLoad) setRefreshing(true);

    try {
      let requestLinesQuery = supabase
        .from('production_request_lines')
        .select('id, logistics_order_id, product_id, product_name, quantity, requested_date, site_id, source_type, created_at')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .gte('requested_date', range.startYMD)
        .lte('requested_date', range.endYMD)
        .order('requested_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (selectedSiteId) {
        requestLinesQuery = requestLinesQuery.eq('site_id', selectedSiteId);
      }

      const [dataset, requestLinesRes] = await Promise.all([
        fetchLogisticsMonthDataset({
          supabase,
          organizationId: profile.organization_id,
          range,
          selectedSiteId,
          filterProductionsBySite: false,
        }),
        requestLinesQuery,
      ]);

      if (requestLinesRes.error) throw requestLinesRes.error;
      const nextRequestLines = filterOperationalProductionRequestLines(requestLinesRes.data || []);

      setOrders(dataset.orders);
      setSaleItems(dataset.saleItems);
      setProductions(dataset.productions);
      setProductionRequestLines(nextRequestLines);
      setScopedProducts(dataset.products || []);

      void reconcileProductionRequestLinesWithOrders({
        supabase,
        organizationId: profile.organization_id,
        lines: nextRequestLines,
        deactivateClosedLogistics: true,
      })
        .then((reconciledRequestLines) => {
          if (reconciledRequestLines.error) {
            console.error(reconciledRequestLines.error);
            return;
          }

          setProductionRequestLines(
            filterOperationalProductionRequestLines(reconciledRequestLines.data || []),
          );
        })
        .catch((reconcileError) => {
          console.error('Error reconciliando panel operativo:', reconcileError);
        });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo sincronizar el panel operativo.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  fetchDataRef.current = fetchData;

  useEffect(() => {
    if (!showAllProducts || !profile?.organization_id || allProductsLoaded) {
      return undefined;
    }

    let cancelled = false;

    const loadAllProducts = async () => {
      try {
        const { data, error } = await applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, is_active, stock_levels(quantity, site_id)')
          .eq('organization_id', profile.organization_id)
        )
          .order('name');

        if (error) throw error;
        if (!cancelled) {
          setAllProducts(data || []);
          setAllProductsLoaded(true);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setAllProductsLoaded(true);
          toast.error('No se pudo cargar el catalogo completo del panel operativo.');
        }
      }
    };

    void loadAllProducts();

    return () => {
      cancelled = true;
    };
  }, [allProductsLoaded, profile?.organization_id, showAllProducts]);

  const itemMapBySaleId = useMemo(
    () => buildSaleItemsBySaleId(saleItems, { operationalOnly: true }),
    [saleItems],
  );
  const visibleProducts = useMemo(
    () => (showAllProducts && allProducts.length > 0 ? allProducts : scopedProducts),
    [allProducts, scopedProducts, showAllProducts],
  );
  const productMap = useMemo(() => buildProductMap(visibleProducts), [visibleProducts]);
  const dailySummary = useMemo(
    () => buildDailyLogisticsSummary({ orders, productions, itemMapBySaleId, productionRequests: productionRequestLines }),
    [itemMapBySaleId, orders, productionRequestLines, productions],
  );

  const selectedDateKey = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDay).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [currentDate, selectedDay]);

  const rows = useMemo(
    () =>
      buildOperationalRowsForDate({
        selectedDateKey,
        orders,
        productionRequests: productionRequestLines,
        productions,
        itemMapBySaleId,
        productMap,
        selectedSiteId,
        carryPreviousBalance: false,
        showAllProducts,
      }),
    [itemMapBySaleId, orders, productMap, productionRequestLines, productions, selectedDateKey, selectedSiteId, showAllProducts],
  );

  const tableRows = useMemo(
    () =>
      rows.map((row) => {
        const rawClosingProjection =
          row.stock + row.productionPending + row.completedProduction - row.pendingOrders;
        const saldo = rawClosingProjection < 0 ? Math.abs(rawClosingProjection) : 0;
        const closingProjection = Math.max(0, rawClosingProjection);

        return {
          ...row,
          saldo,
          closingProjection,
        };
      }),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const filteredRows = tableRows.filter((row) =>
      OPERATIONAL_COLUMNS.every((column) => {
        const filterValue = columnFilters[column.key];
        if (!String(filterValue || '').trim()) return true;

        if (column.type === 'text') {
          return String(row[column.key] || '').toLowerCase().includes(String(filterValue).trim().toLowerCase());
        }

        return matchesNumericFilter(Number(row[column.key] || 0), filterValue);
      }),
    );

    return [...filteredRows].sort((left, right) => {
      const leftValue = left[sortConfig.key];
      const rightValue = right[sortConfig.key];

      let comparison = 0;
      if (sortConfig.key === 'name') {
        comparison = String(leftValue || '').localeCompare(String(rightValue || ''), 'es', { sensitivity: 'base' });
      } else {
        comparison = Number(leftValue || 0) - Number(rightValue || 0);
      }

      if (comparison === 0) {
        comparison = String(left.name || '').localeCompare(String(right.name || ''), 'es', { sensitivity: 'base' });
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [columnFilters, sortConfig, tableRows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          stock: acc.stock + row.stock,
          sentToProduction: acc.sentToProduction + row.sentToProduction,
          productionPending: acc.productionPending + row.productionPending,
          completedProduction: acc.completedProduction + row.completedProduction,
          pendingOrders: acc.pendingOrders + row.pendingOrders,
          saldo:
            acc.saldo +
            Math.max(
              row.pendingOrders - (row.stock + row.productionPending + row.completedProduction),
              0,
            ),
          closingProjection:
            acc.closingProjection +
            Math.max(
              row.stock + row.productionPending + row.completedProduction - row.pendingOrders,
              0,
            ),
        }),
        { stock: 0, sentToProduction: 0, productionPending: 0, completedProduction: 0, pendingOrders: 0, saldo: 0, closingProjection: 0 },
      ),
    [rows],
  );

  const visibleTotals = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => ({
          stock: acc.stock + row.stock,
          sentToProduction: acc.sentToProduction + row.sentToProduction,
          productionPending: acc.productionPending + row.productionPending,
          completedProduction: acc.completedProduction + row.completedProduction,
          pendingOrders: acc.pendingOrders + row.pendingOrders,
          saldo: acc.saldo + (row.saldo || 0),
          closingProjection: acc.closingProjection + row.closingProjection,
        }),
        { stock: 0, sentToProduction: 0, productionPending: 0, completedProduction: 0, pendingOrders: 0, saldo: 0, closingProjection: 0 },
      ),
    [visibleRows],
  );

  const selectedInfo = dailySummary[selectedDay] || EMPTY_DAY;
  const selectedSiteName = selectedSiteId ? sitios.find((site) => site.id === selectedSiteId)?.nombre || 'Sitio' : 'Consolidado';
  const selectedDateLabel = new Date(`${selectedDateKey}T12:00:00`).toLocaleDateString('es-AR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

  const handleSelectedDateChange = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return;

    const [year, month, day] = normalized.split('-').map(Number);
    if (!year || !month || !day) return;

    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDay(day);
  };

  const handleSort = (key) => {
    const column = OPERATIONAL_COLUMNS.find((item) => item.key === key);

    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key,
        direction: column?.defaultDirection || 'asc',
      };
    });
  };

  const handleFilterChange = (key, value) => {
    setColumnFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const hasActiveFilters = Object.values(columnFilters).some((value) => String(value || '').trim());
  const activeFilterCount = Object.values(columnFilters).filter((value) => String(value || '').trim()).length;

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando panel operativo...</p>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Package size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Panel Operativo</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Vista diaria por producto con orden y filtros.</p>
          </div>
        </div>

        <div className="imp-header-actions">
          <div
            title="Filtra el panel por sucursal o sitio logistico."
            className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm"
          >
            <MapPin size={14} className="text-slate-400" />
            <select value={selectedSiteId} onChange={(event) => setSelectedSiteId(event.target.value)} className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer">
              <option value="">Consolidado</option>
              {sitios.map((site) => <option key={site.id} value={site.id}>{site.nombre}</option>)}
            </select>
          </div>

          <div
            title="Elige la fecha operativa que queres analizar."
            className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm"
          >
            <CalendarDays size={14} className="text-slate-400" />
            <input
              type="date"
              value={selectedDateKey}
              onChange={(event) => handleSelectedDateChange(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer"
            />
          </div>

          <button
            type="button"
            title="Recarga pedidos, solicitudes de produccion, fabricaciones y stock."
            onClick={fetchData}
            disabled={refreshing}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      <div className="imp-kpis mb-3">
        <div className="imp-kpi" title="Cantidad de unidades que todavia falta entregar hoy.">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pedidos del dia</p>
            <ShoppingBag size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(totals.pendingOrders)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{selectedInfo.ordersCount} pedidos programados</p>
        </div>

        <div className="imp-kpi" title="Unidades solicitadas hoy a produccion para este dia logistico.">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Enviado a produccion</p>
            <Factory size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(totals.sentToProduction)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pedido cursado para este dia</p>
        </div>

        <div className="imp-kpi" title="Unidades que ya se completaron en produccion para este dia operativo.">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Produccion completada</p>
            <Factory size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{fmt(totals.completedProduction)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {fmt(totals.productionPending)} aun pendientes de fabricar
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p title="Resumen diario por producto para la fecha y el sitio elegidos." className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vista operativa</p>
                <span className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-600">
                  {selectedDateLabel}
                </span>
                <span className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {selectedSiteName}
                </span>
              </div>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {selectedInfo.ordersCount} pedido(s) - {fmt(selectedInfo.pendingUnits)}u pedidas - {fmt(selectedInfo.productionUnits)}u completadas
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <div title="Cantidad de productos que quedan visibles en la tabla con los filtros actuales." className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Resultados visibles</p>
                <p className="text-[11px] font-bold text-slate-500">
                  {visibleRows.length} producto(s)
                  {hasActiveFilters ? ` - ${activeFilterCount} filtro(s)` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAllProducts((current) => !current)}
                title="Alterna entre todos los productos del catalogo y solo los que tienen movimiento enviado a produccion."
                className={`rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                  showAllProducts
                    ? 'border-[#51A7FD] bg-sky-50 text-[#0B2A36]'
                    : 'border-slate-200 bg-slate-50 text-slate-500 hover:border-[#51A7FD] hover:text-[#0B2A36]'
                }`}
              >
                {showAllProducts ? 'Todos los productos' : 'Solo enviados'}
              </button>

              <button
                type="button"
                onClick={() => setFiltersOpen((current) => !current)}
                title={filtersOpen ? 'Ocultar filtros por columna.' : 'Mostrar filtros por columna.'}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-all ${
                  hasActiveFilters || filtersOpen
                    ? 'border-[#51A7FD] bg-sky-50 text-[#0B2A36]'
                    : 'border-slate-200 bg-slate-50 text-slate-500 hover:border-[#51A7FD] hover:text-[#0B2A36]'
                }`}
                aria-label={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
              >
                {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50">
                <tr className="text-[9px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
                  {OPERATIONAL_COLUMNS.map((column) => {
                    const sorted = sortConfig.key === column.key;
                    const alignClass = column.align === 'right' ? 'justify-end text-right' : 'justify-start text-left';

                    return (
                      <th key={column.key} className={`px-4 py-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
                        <div className={`relative inline-flex items-center gap-2 ${alignClass} group`}>
                          <button
                            type="button"
                            onClick={() => handleSort(column.key)}
                            className="inline-flex items-center gap-1 transition-colors hover:text-slate-700"
                          >
                            <span>{column.label}</span>
                            {sorted ? (
                              sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                            ) : (
                              <ArrowUpDown size={12} className="text-slate-300" />
                            )}
                          </button>

                          <div className={`pointer-events-none absolute top-[calc(100%+8px)] z-20 w-56 rounded-2xl bg-[#0B2A36] px-3 py-2 text-[10px] font-bold normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-xl transition-all duration-150 group-hover:opacity-100 ${column.align === 'right' ? 'right-0' : 'left-0'}`}>
                            {column.tooltip}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>

                {filtersOpen ? (
                  <tr className="border-b border-slate-100 bg-white">
                    {OPERATIONAL_COLUMNS.map((column) => (
                      <th key={`${column.key}-filter`} className={`px-4 py-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
                        <input
                          type="text"
                          inputMode={column.type === 'number' ? 'decimal' : 'text'}
                          value={columnFilters[column.key]}
                          onChange={(event) => handleFilterChange(column.key, event.target.value)}
                          placeholder={column.type === 'number' ? 'Ej: >=10' : 'Filtrar'}
                          className={`w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-600 outline-none transition-all focus:border-[#51A7FD] focus:bg-white ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                        />
                      </th>
                    ))}
                  </tr>
                ) : null}
              </thead>
              <tbody className="divide-y divide-slate-50">
                {visibleRows.length > 0 ? (
                  visibleRows.map((row) => (
                    <tr key={row.key} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-[11px] font-black uppercase tracking-tight text-slate-800">{row.name}</p>
                      </td>
                      <td className={`px-3 py-3 text-right text-[11px] font-black ${row.stock < 0 ? 'text-rose-600' : 'text-slate-600'}`}>{fmt(row.stock)}</td>
                      <td className={`px-3 py-3 text-right text-[11px] font-black ${row.sentToProduction < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmt(row.sentToProduction)}</td>
                      <td className={`px-3 py-3 text-right text-[11px] font-black ${row.productionPending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{fmt(row.productionPending)}</td>
                      <td className={`px-3 py-3 text-right text-[11px] font-black ${row.completedProduction > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{fmt(row.completedProduction)}</td>
                      <td className={`px-3 py-3 text-right text-[11px] font-black ${row.pendingOrders < 0 ? 'text-rose-600' : 'text-[#51A7FD]'}`}>{fmt(row.pendingOrders)}</td>
                      <td className={`px-4 py-3 text-right text-[11px] font-black ${row.saldo > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmt(row.saldo)}</td>
                      <td className="px-4 py-3 text-right text-[11px] font-black text-emerald-600">{fmt(row.closingProjection)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-slate-300">
                      <div className="flex flex-col items-center gap-2">
                        <Package size={28} />
                        <p className="text-[10px] font-black uppercase tracking-widest">Sin productos para este dia</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              {visibleRows.length > 0 ? (
                <tfoot className="bg-slate-50 border-t border-slate-100">
                  <tr className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <td className="px-4 py-3">Totales visibles</td>
                    <td className={`px-3 py-3 text-right ${visibleTotals.stock < 0 ? 'text-rose-600' : 'text-slate-700'}`}>{fmt(visibleTotals.stock)}</td>
                    <td className={`px-3 py-3 text-right ${visibleTotals.sentToProduction < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmt(visibleTotals.sentToProduction)}</td>
                    <td className={`px-3 py-3 text-right ${visibleTotals.productionPending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{fmt(visibleTotals.productionPending)}</td>
                    <td className={`px-3 py-3 text-right ${visibleTotals.completedProduction > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{fmt(visibleTotals.completedProduction)}</td>
                    <td className={`px-3 py-3 text-right ${visibleTotals.pendingOrders < 0 ? 'text-rose-600' : 'text-[#51A7FD]'}`}>{fmt(visibleTotals.pendingOrders)}</td>
                     <td className={`px-4 py-3 text-right ${visibleTotals.saldo > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmt(visibleTotals.saldo)}</td>
                     <td className="px-4 py-3 text-right text-emerald-600">{fmt(visibleTotals.closingProjection)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
